import type { Locale } from "./locales";

export type CatalogFields = {
  name?: string;
  shortName?: string;
  subtitle?: string;
  description?: string;
};
export type CatalogText = Record<string, CatalogFields>;

const loaders = {
  en: () => import("./catalog-text/en.json"),
  sv: () => import("./catalog-text/sv.json"),
  de: () => import("./catalog-text/de.json"),
};
const text: Partial<Record<Locale, CatalogText>> = {};
const pending = new Map<Locale, Promise<void>>();
const listeners = new Set<() => void>();
let revision = 0;

export function getCatalogText(locale: Locale): CatalogText | undefined {
  return text[locale];
}

/** Build-time renderers can install English without adding it to browser imports. */
export function registerCatalogText(locale: Locale, value: CatalogText): void {
  if (text[locale] === value) return;
  text[locale] = value;
  revision++;
  listeners.forEach((listener) => listener());
}

export function subscribeCatalogText(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function catalogTextRevision(): number {
  return revision;
}

function loadLocale(locale: Locale): Promise<void> {
  if (text[locale]) return Promise.resolve();
  const existing = pending.get(locale);
  if (existing) return existing;
  const request = loaders[locale]()
    .then(({ default: value }) => registerCatalogText(locale, value))
    .finally(() => { pending.delete(locale); });
  pending.set(locale, request);
  return request;
}

/** Only the requested language and its English fallback. Failed loads can retry. */
export function loadCatalogText(locale: Locale): Promise<void> {
  return Promise.all(locale === "en" ? [loadLocale("en")] : [loadLocale("en"), loadLocale(locale)])
    .then(() => undefined);
}
